import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { MasterDocumentAuditModal } from './MasterDocumentAuditModal';
import { useMasterDocumentTranslation } from '../i18n';
import type { AuditHistoryProps } from '../../../audit/types/audit.types';

/**
 * Contexto que dispara la apertura del historial de auditoría de un documento.
 * `source` distingue desde dónde se invoca (detalle vs. listado); `documentId`
 * es el id de la entidad cuyo historial se consulta.
 */
export interface AuditContext {
  source: 'detail' | 'list';
  documentId: string;
}

/**
 * API imperativa expuesta por el `MasterDocumentProvider` cuando se pasa la prop
 * `auditHistory`. La consumen `SmartMasterDocument` (barra del detalle) y, si se
 * desea, `DocumentList` (menú de fila) — igual patrón que la impresión nativa.
 */
export interface MasterDocumentAuditApi {
  openAudit(ctx: AuditContext): void;
  isAuditAvailable(ctx: AuditContext): boolean;
  label: string;
}

// null = auditoría no configurada en el provider → subsistema inerte (retrocompat).
const AuditApiContext = createContext<MasterDocumentAuditApi | null>(null);

/**
 * Devuelve la API de auditoría del `MasterDocumentProvider`, o `null` si no se pasó
 * la prop `auditHistory` (auditoría no disponible → retrocompatibilidad total).
 */
export function useMasterDocumentAudit(): MasterDocumentAuditApi | null {
  return useContext(AuditApiContext);
}

interface MasterDocumentAuditProviderProps {
  /**
   * Configuración de auditoría (opcional). Mismo contrato que consume MasterCrud.
   * Cuando se provee y `enabled` es true, el detalle del documento muestra una
   * acción ⏱ que abre el historial. Sin esta prop el subsistema queda inerte y el
   * comportamiento es idéntico al actual → retrocompatibilidad total.
   */
  auditHistory?: AuditHistoryProps;
  children: ReactNode;
}

/**
 * Host de auditoría: posee el estado `{ open, entityId }`, monta UN único
 * `MasterDocumentAuditModal` y expone la API imperativa por contexto. Cuando no se
 * pasa `auditHistory` (o `enabled` es false), el contexto queda en `null` y no se
 * renderiza ninguna acción ni modal.
 */
export function MasterDocumentAuditProvider({
  auditHistory,
  children,
}: MasterDocumentAuditProviderProps) {
  const { t } = useMasterDocumentTranslation();
  const auditLabel = t('master_document.document.auditTitle');
  const [state, setState] = useState<{ open: boolean; entityId: string | null }>({
    open: false,
    entityId: null,
  });

  // La acción se MUESTRA si está habilitada por permiso (enabled) y el documento
  // está guardado (tiene id). Mismo criterio que la impresión.
  const isAuditAvailable = useCallback(
    (ctx: AuditContext): boolean => {
      if (!auditHistory) return false;
      if (!auditHistory.enabled) return false;
      if (!ctx.documentId) return false;
      return true;
    },
    [auditHistory],
  );

  const openAudit = useCallback(
    (ctx: AuditContext) => {
      if (!auditHistory || !ctx.documentId) return;
      setState({ open: true, entityId: ctx.documentId });
    },
    [auditHistory],
  );

  const close = useCallback(() => setState({ open: false, entityId: null }), []);

  const api = useMemo<MasterDocumentAuditApi | null>(() => {
    if (!auditHistory) return null;
    return { openAudit, isAuditAvailable, label: auditLabel };
  }, [auditHistory, openAudit, isAuditAvailable, auditLabel]);

  return (
    <AuditApiContext.Provider value={api}>
      {children}
      {auditHistory && state.entityId && (
        <MasterDocumentAuditModal
          isOpen={state.open}
          title={auditLabel}
          onClose={close}
          entityId={state.entityId}
          fetchFn={auditHistory.fetchFn}
        />
      )}
    </AuditApiContext.Provider>
  );
}
